import React, { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Calendar, Clock, User, Activity, Mail, Settings, Database, ExternalLink, AlertCircle, CheckCircle, XCircle, Info } from 'lucide-react'
import { supabase } from '@/integrations/supabase/client'
import { formatDistanceToNow, format } from 'date-fns'

interface MailboxHistoryEvent {
  id: string
  action_type: string
  field_name: string | null
  old_value: any
  new_value: any
  created_at: string
  changed_by_name: string | null
  changed_by_email: string | null
  change_source: string
  metadata: any
}

interface MailboxHistoryDialogProps {
  mailbox: any
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function MailboxHistoryDialog({ mailbox, open, onOpenChange }: MailboxHistoryDialogProps) {
  const [historyEvents, setHistoryEvents] = useState<MailboxHistoryEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open && mailbox?.id) {
      fetchMailboxHistory()
    }
  }, [open, mailbox?.id])

  const fetchMailboxHistory = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // Fetch audit logs for this mailbox
      const { data: auditLogs, error: auditError } = await supabase
        .from('audit_logs')
        .select('*')
        .eq('table_name', 'mailboxes')
        .eq('record_id', mailbox.id)
        .order('created_at', { ascending: false })

      if (auditError) {
        console.error('Error fetching audit logs:', auditError)
        setError('Failed to load audit history')
        return
      }

      // Create synthetic events for important dates
      const syntheticEvents: MailboxHistoryEvent[] = []
      
      // Add creation date
      if (mailbox.created_at) {
        syntheticEvents.push({
          id: `created-${mailbox.id}`,
          action_type: 'created',
          field_name: null,
          old_value: null,
          new_value: null,
          created_at: mailbox.created_at,
          changed_by_name: null,
          changed_by_email: null,
          change_source: 'system',
          metadata: { type: 'mailbox_created' }
        })
      }

      // Add status changes
      if (mailbox.status_updated_at) {
        syntheticEvents.push({
          id: `status-${mailbox.id}`,
          action_type: 'status_change',
          field_name: 'status',
          old_value: null,
          new_value: mailbox.status,
          created_at: mailbox.status_updated_at,
          changed_by_name: null,
          changed_by_email: null,
          change_source: 'system',
          metadata: { type: 'status_update' }
        })
      }

      // Add warmup enabled date
      if (mailbox.warmup_enabled_date) {
        syntheticEvents.push({
          id: `warmup-enabled-${mailbox.id}`,
          action_type: 'warmup_enabled',
          field_name: 'warmup_status',
          old_value: 'INACTIVE',
          new_value: 'ACTIVE',
          created_at: mailbox.warmup_enabled_date,
          changed_by_name: null,
          changed_by_email: null,
          change_source: 'smartlead',
          metadata: { type: 'warmup_enabled' }
        })
      }

      // Add last sync date
      if (mailbox.last_api_synced_at) {
        syntheticEvents.push({
          id: `sync-${mailbox.id}`,
          action_type: 'sync',
          field_name: 'api_sync_status',
          old_value: null,
          new_value: mailbox.api_sync_status,
          created_at: mailbox.last_api_synced_at,
          changed_by_name: null,
          changed_by_email: null,
          change_source: 'smartlead',
          metadata: { type: 'api_sync' }
        })
      }

      // Combine and sort all events
      const allEvents = [...(auditLogs || []), ...syntheticEvents]
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())

      setHistoryEvents(allEvents)
    } catch (err) {
      console.error('Error fetching mailbox history:', err)
      setError('Failed to load mailbox history')
    } finally {
      setLoading(false)
    }
  }

  const getEventIcon = (event: MailboxHistoryEvent) => {
    switch (event.action_type) {
      case 'created':
        return <Database className="h-4 w-4 text-blue-500" />
      case 'status_change':
        return <Activity className="h-4 w-4 text-green-500" />
      case 'warmup_enabled':
        return <CheckCircle className="h-4 w-4 text-orange-500" />
      case 'sync':
        return <ExternalLink className="h-4 w-4 text-purple-500" />
      case 'update':
        return <Settings className="h-4 w-4 text-gray-500" />
      default:
        return <Info className="h-4 w-4 text-gray-400" />
    }
  }

  const getEventTitle = (event: MailboxHistoryEvent) => {
    switch (event.action_type) {
      case 'created':
        return 'Mailbox Created'
      case 'status_change':
        return `Status Changed to ${event.new_value}`
      case 'warmup_enabled':
        return 'Warmup Enabled'
      case 'sync':
        return `Synced with Smartlead (${event.new_value})`
      case 'update':
        return `${event.field_name} Updated`
      default:
        return event.action_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    }
  }

  const getEventDescription = (event: MailboxHistoryEvent) => {
    switch (event.action_type) {
      case 'created':
        return 'Mailbox was initially created in the system'
      case 'status_change':
        return `Mailbox status was updated to ${event.new_value}`
      case 'warmup_enabled':
        return 'Warmup functionality was enabled for this mailbox'
      case 'sync':
        return `Last synchronization with Smartlead platform (Status: ${event.new_value})`
      case 'update':
        if (event.old_value && event.new_value) {
          return `Changed from "${event.old_value}" to "${event.new_value}"`
        }
        return `Updated ${event.field_name}`
      default:
        return event.metadata?.description || 'System event'
    }
  }

  const getEventBadgeVariant = (event: MailboxHistoryEvent) => {
    switch (event.action_type) {
      case 'created':
        return 'default'
      case 'status_change':
        return 'secondary'
      case 'warmup_enabled':
        return 'destructive'
      case 'sync':
        return 'outline'
      default:
        return 'outline'
    }
  }

  const formatEventTime = (dateString: string) => {
    const date = new Date(dateString)
    return {
      relative: formatDistanceToNow(date, { addSuffix: true }),
      absolute: format(date, 'MMM dd, yyyy HH:mm:ss')
    }
  }

  if (!mailbox) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Mailbox History - {mailbox.email}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Mailbox Summary */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Mailbox Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="font-medium text-muted-foreground">Created</div>
                  <div>{mailbox.created_at ? format(new Date(mailbox.created_at), 'MMM dd, yyyy') : 'Unknown'}</div>
                </div>
                <div>
                  <div className="font-medium text-muted-foreground">Status</div>
                  <Badge variant={mailbox.status === 'ACTIVE' ? 'default' : 'secondary'}>
                    {mailbox.status}
                  </Badge>
                </div>
                <div>
                  <div className="font-medium text-muted-foreground">Warmup</div>
                  <Badge variant={mailbox.warmup_status === 'ACTIVE' ? 'destructive' : 'outline'}>
                    {mailbox.warmup_status || 'Inactive'}
                  </Badge>
                </div>
                <div>
                  <div className="font-medium text-muted-foreground">Last Sync</div>
                  <div>{mailbox.last_api_synced_at ? formatDistanceToNow(new Date(mailbox.last_api_synced_at), { addSuffix: true }) : 'Never'}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* History Events */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Event History</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  <p className="text-muted-foreground mt-2">Loading history...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
                  <p className="text-destructive">{error}</p>
                  <Button onClick={fetchMailboxHistory} variant="outline" className="mt-2">
                    Retry
                  </Button>
                </div>
              ) : historyEvents.length === 0 ? (
                <div className="text-center py-8">
                  <Info className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                  <p className="text-muted-foreground">No history events found</p>
                </div>
              ) : (
                <ScrollArea className="h-96">
                  <div className="space-y-4">
                    {historyEvents.map((event, index) => {
                      const timeInfo = formatEventTime(event.created_at)
                      return (
                        <div key={event.id} className="flex gap-4 p-4 border rounded-lg hover:bg-muted/50 transition-colors">
                          <div className="flex-shrink-0 mt-1">
                            {getEventIcon(event)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-medium text-sm">{getEventTitle(event)}</h4>
                              <div className="flex items-center gap-2">
                                <Badge variant={getEventBadgeVariant(event)} className="text-xs">
                                  {event.change_source}
                                </Badge>
                                <span className="text-xs text-muted-foreground" title={timeInfo.absolute}>
                                  {timeInfo.relative}
                                </span>
                              </div>
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">
                              {getEventDescription(event)}
                            </p>
                            {event.changed_by_name && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <User className="h-3 w-3" />
                                {event.changed_by_name}
                                {event.changed_by_email && ` (${event.changed_by_email})`}
                              </div>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  )
}



